import React from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add,
  Remove,
} from '@mui/icons-material';

import DOMPurify from 'dompurify';

/**
 * Options are stored as HTML, which leaked into the plain input as literal
 * "<p>...</p>" text. A value that is nothing more than one paragraph of text is
 * edited as text and written back wrapped; anything richer — formatting, or an
 * equation — is left exactly as it is rather than being flattened.
 */
const SIMPLE_PARAGRAPH = /^<p>([^<>]*)<\/p>$/;

const toEditable = (html: string): string => {
  const match = html.trim().match(SIMPLE_PARAGRAPH);
  return match ? match[1] : html;
};

const isSimple = (html: string): boolean =>
  html.trim() === '' || SIMPLE_PARAGRAPH.test(html.trim()) || !/[<>]/.test(html);

const toStored = (text: string): string => (text.trim() === '' ? '' : `<p>${text}</p>`);

interface AnswerOptionsSectionProps {
  options: string[];
  correctAnswerIndex: number;
  onOptionsChange: (options: string[]) => void;
  onCorrectAnswerChange: (index: number) => void;
}

const AnswerOptionsSection: React.FC<AnswerOptionsSectionProps> = ({
  options,
  correctAnswerIndex,
  onOptionsChange,
  onCorrectAnswerChange,
}) => {
  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onOptionsChange(newOptions);
  };

  const handleAddOption = () => {
    if (options.length < 6) { // Maximum 6 options
      onOptionsChange([...options, '']);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (options.length > 2) { // Minimum 2 options
      const newOptions = options.filter((_, i) => i !== index);
      onOptionsChange(newOptions);
      
      // Adjust correct answer index if necessary
      if (correctAnswerIndex >= newOptions.length) {
        onCorrectAnswerChange(newOptions.length - 1);
      } else if (correctAnswerIndex === index) {
        onCorrectAnswerChange(0);
      } else if (correctAnswerIndex > index) {
        onCorrectAnswerChange(correctAnswerIndex - 1);
      }
    }
  };

  const getOptionLabel = (index: number) => {
    return String.fromCharCode(65 + index); // A, B, C, D, E, F
  };

  const hasEmptyOptions = options.some(option => !option.trim());
  const hasCorrectAnswer = correctAnswerIndex >= 0 && correctAnswerIndex < options.length;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        📝 Answer Options
      </Typography>

      {hasEmptyOptions && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please fill in all answer options before saving.
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        {options.map((option, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ 
                minWidth: 40,
                textAlign: 'center',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              {getOptionLabel(index)})
            </Typography>
            
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={toEditable(option)}
              onChange={(e) =>
                handleOptionChange(index, isSimple(option) ? toStored(e.target.value) : e.target.value)
              }
              placeholder={`Enter option ${getOptionLabel(index)}`}
              sx={{ mx: 2 }}
            />
            
            {options.length > 2 && (
              <IconButton
                color="error"
                onClick={() => handleRemoveOption(index)}
                size="small"
              >
                <Remove />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      {/* Add/Remove Options */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {options.length < 6 && (
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddOption}
            size="small"
          >
            Add Option
          </Button>
        )}
        
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {options.length}/6 options • Minimum 2, Maximum 6
        </Typography>
      </Box>

      {/* Correct Answer Selection */}
      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
          ✅ Correct Answer
        </FormLabel>
        
        <RadioGroup
          value={correctAnswerIndex.toString()}
          onChange={(e) => onCorrectAnswerChange(parseInt(e.target.value))}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={index.toString()}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                      {getOptionLabel(index)}
                    </Typography>
                    {option ? (
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(option) }}
                      />
                    ) : (
                      <Typography variant="body2">{`Option ${getOptionLabel(index)}`}</Typography>
                    )}
                  </Box>
                }
                disabled={!option.trim()}
                sx={{ 
                  border: 1,
                  borderColor: correctAnswerIndex === index ? 'success.main' : 'grey.300',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  m: 0,
                  bgcolor: correctAnswerIndex === index ? 'success.50' : 'transparent',
                }}
              />
            ))}
          </Box>
        </RadioGroup>
        
        {!hasCorrectAnswer && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            Please select the correct answer.
          </Typography>
        )}
      </FormControl>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        💡 Tip: Make sure all options are plausible but only one is clearly correct. 
        Avoid "All of the above" or "None of the above" unless necessary.
      </Typography>
    </Paper>
  );
};

export default AnswerOptionsSection;
